"use client";

import { useState, useEffect } from "react";
import {
  Button,
  HStack,
  Image,
  Input,
  Link,
  Stack,
  Text,
  Box,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Field } from "../ui/field";
import { PasswordInput } from "../ui/password-input";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../Images/logo.png";
import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import { signup } from "../../Redux/Action/auth/signupAction";
import { CountrySelector } from "./CountrySelector";
import { TermsAndConditions } from "./TermsAndConditions";
import { getLocalStorageItem } from "../../Utils/Helper";
import { CommonButton } from "../ui/CommonButton";
import { useThemeContext } from "../../Context/ThemeContext";
import { ColorModeButton } from "../ui/color-mode";

export const SignupForm = ({ referralCode }) => {

  const { resolvedTheme } = useTheme();
  const { themeConfig } = useThemeContext();
  const theme = themeConfig[resolvedTheme === 'dark' ? 'dark' : 'light'];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const whitelabelData = getLocalStorageItem("whitelabelData")
  const loading = useSelector((state) => state?.GetWhitelabel?.loading);
  const [logoSrc, setLogoSrc] = useState(null);

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, md: false });
  const stackSpacing = useBreakpointValue({ base: 4, md: 5 });
  const formPadding = useBreakpointValue({ base: "1rem", sm: "1.5rem", md: "2rem" });
  const fieldSpacing = useBreakpointValue({ base: 3, md: 5 });
  const buttonWidth = useBreakpointValue({ base: "100%", md: "auto" });
  const formMaxWidth = useBreakpointValue({ base: "100%", sm: "450px", md: "600px" });


  useEffect(() => {
    if (!loading) {
      const selectedLogo =
        whitelabelData && resolvedTheme === "dark"
          ? whitelabelData.logo_dark
          : whitelabelData?.logo_light;

      setLogoSrc(selectedLogo || logo);
    }
  }, [loading, resolvedTheme]);
  const [currentCountryCode, setCurrentCountryCode] = useState("+971");
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    password: "",
    cpassword: "",
    countryCode: currentCountryCode,
    mobile: "",
    curr: "USD",
    actype: "LIVE",
    utype: "CLIENT",
    ref: "",
    promoCode: "",
    referralBy: referralCode || ""
  });

  // Update formData when currentCountryCode changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      countryCode: currentCountryCode
    }));
  }, [currentCountryCode]);

  const [touchedPassword, setTouchedPassword] = useState(false);
  const [touchedCPassword, setTouchedCPassword] = useState(false);
  const [errors, setErrors] = useState({
    fname: "",
    lname: "",
    email: "",
    password: "",
    cpassword: "",
    mobile: "",
    curr: "",
    actype: "",
    utype: "",
    ref: "",
    promoCode: "",
    referralCode: ""
  });

  const [isDisabled, setIsDisabled] = useState(true);
  const [isChecked, setIsChecked] = useState(true);
  const [passwordMissmatched, setPasswordMisssmatched] = useState(false);
  const [isEnteringCP, setIsEnteringCP] = useState(false);

  const [showRoleModal, setShowRoleModal] = useState(false);

  const whitelabelDetails = getLocalStorageItem("whitelabelData");

  const handleChange = (e) => {

    const { name, value } = e?.target || {};
    const nameRegex = /^[A-Za-z]*$/;
    const mobileRegex = /^[0-9]{0,15}$/;

    if (name === "fname" || name === "lname") {
      if (!nameRegex.test(value)) return;
    }

    if (name === "mobile") {
      if (value.includes("+") && value.indexOf("+") !== 0) return;
      if (!mobileRegex.test(value)) return;
    }

    setFormData((prev) => {
      const updatedForm = { ...prev, [name]: value };
      if (name === "password" || name === "cpassword") {
        setPasswordMisssmatched(updatedForm.password !== updatedForm.cpassword);
      }

      return updatedForm;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    const { fname, lname, email, password, cpassword, mobile } = formData || {};
    let hasError = false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#_!])[A-Za-z\d@#_!]{8,}$/;
    const mobileRegex = /^[0-9]{8,15}$/;

    if (!fname) {
      setErrors((prev) => ({ ...prev, fname: "First name is required." }));
      hasError = true;
    }
    if (!lname) {
      setErrors((prev) => ({ ...prev, lname: "Last name is required." }));
      hasError = true;
    }
    if (!email || !emailRegex.test(email)) {
      setErrors((prev) => ({ ...prev, email: "Valid E-mail is required." }));
      hasError = true;
    }
    if (!password) {
      setErrors((prev) => ({ ...prev, password: "Password is required." }));
      hasError = true;
    } else if (!passwordRegex.test(password)) {
      setErrors((prev) => ({
        ...prev,
        password: "Password should be minimum 8 characters, with uppercase, lowercase, numbers and a special character (@,#,_,!)",
      }));
      hasError = true;
    }
    if (!cpassword) {
      setErrors((prev) => ({ ...prev, cpassword: "Please re-enter your password." }));
      hasError = true;
    } else if (passwordMissmatched) {
      setErrors((prev) => ({ ...prev, cpassword: "Passwords do not match." }));
      hasError = true;
    }
    if (!mobile || !mobileRegex.test(mobile)) {
      setErrors((prev) => ({ ...prev, mobile: "Enter valid mobile no." }));
      hasError = true;
    }

    if (!hasError) {
      setShowRoleModal(true);
    }
  };

  const handleRoleSelect = (roleLabel) => {
    const role = roleLabel === "IB" ? 6 : 7;
    const updatedForm = {
      ...formData,
      role,
      ...(whitelabelDetails?._id != null && { whitelabelID: whitelabelDetails._id }),
    };
    setShowRoleModal(false);
    setIsDisabled(true);
  
    
    dispatch(
      signup({
        payload: updatedForm,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            setIsDisabled(false);
            navigate("/verifyemail");
          } else {
            setIsDisabled(false);
          }
        },
      })
    );
  };

  useEffect(() => {
    const { fname, lname, email, password, cpassword, mobile } = formData || {};
    if (
      fname &&
      lname &&
      email &&
      password &&
      cpassword &&
      mobile &&
      !errors?.fname &&
      !errors?.lname &&
      !errors?.email &&
      !errors?.password &&
      !errors?.cpassword &&
      !errors?.mobile
    ) {
      setIsDisabled(false);
    }
  }, [formData, errors]);

  return (
    <>
      <Box position="absolute" top="4" right="4" zIndex="10">
        <ColorModeButton />
      </Box>
      <form onSubmit={handleSubmit}>
        <Stack
          gap={stackSpacing}
          align="center"
          maxW={formMaxWidth}
          width="100%"
          margin="auto"
          borderRadius="2xl"
          padding={formPadding}
          bg={theme.bgCard}
          boxShadow={theme.shadowElevated}
          border="1px solid"
          borderColor={theme.borderPrimary}
          transition="all 0.3s ease"
          overflowX="hidden"
          mb={8}
        >
          <Image src={logoSrc} alt="Logo" mb={4} maxH="60px" maxWidth="100%" />
          
          {/* Name fields - stacked on mobile, horizontal on desktop */}
          <Stack 
            width="100%" 
            direction={isMobile ? "column" : "row"} 
            spacing={fieldSpacing}
          >
            <Field 
              label="First Name" 
              invalid={!!errors?.fname} 
              errorText={errors?.fname} 
              flex={1}
              width="100%"
            >
              <Input 
                px={2} 
                name="fname" 
                value={formData?.fname} 
                onChange={handleChange} 
                width="100%"
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.inputText}
                _placeholder={{ color: theme.inputPlaceholder }}
                _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
              />
            </Field>
            <Field 
              label="Last Name" 
              invalid={!!errors?.lname} 
              errorText={errors?.lname} 
              flex={1}
              width="100%"
            >
              <Input 
                px={2} 
                name="lname" 
                value={formData?.lname} 
                onChange={handleChange} 
                width="100%"
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.inputText}
                _placeholder={{ color: theme.inputPlaceholder }}
                _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
              />
            </Field>
          </Stack>
          
          <Field 
            label="Email" 
            invalid={!!errors?.email} 
            errorText={errors?.email} 
            width="100%"
          >
            <Input 
              px={2} 
              name="email" 
              value={formData?.email} 
              onChange={handleChange} 
              width="100%"
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              color={theme.inputText}
              _placeholder={{ color: theme.inputPlaceholder }}
              _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
            />
          </Field>

          <Stack width="100%" spacing={3}>
            {/* Password fields - stacked on mobile, horizontal on desktop */}
            <Stack 
              width="100%" 
              direction={isMobile ? "column" : "row"} 
              spacing={fieldSpacing}
              align="start"
            >
              <Field 
                label="Password" 
                invalid={!!errors?.password} 
                flex={1}
                width="100%"
              >
                <PasswordInput
                  px={2}
                  name="password"
                  value={formData?.password}
                  onBlur={() => setTouchedPassword(true)}
                  onChange={handleChange}
                  width="100%"
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                  color={theme.inputText}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
                />
              </Field>
              <Field
                label="Confirm Password"
                invalid={!!errors?.cpassword || passwordMissmatched}
                color={isEnteringCP ? (passwordMissmatched ? "red.500" : "green.500") : ""}
                flex={1}
                width="100%"
              >
                <PasswordInput
                  px={2}
                  name="cpassword"
                  value={formData?.cpassword}
                  onFocus={() => setIsEnteringCP(true)}
                  onBlur={() => {
                    setIsEnteringCP(false);
                    setTouchedCPassword(true);
                  }}
                  onChange={handleChange}
                  width="100%"
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                  color={theme.inputText}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
                />
              </Field>
            </Stack>

            {(touchedPassword && touchedCPassword && (errors?.password || errors?.cpassword || passwordMissmatched)) && (
              <Box width="100%">
                <Text fontSize="sm" color="red.500">
                  {(errors?.password && !passwordMissmatched && errors?.password) ||
                    (errors?.cpassword && errors?.cpassword) ||
                    (passwordMissmatched && "Passwords do not match.")}
                </Text>
              </Box>
            )}
          </Stack>

          <Box width="100%">
            {/* Country code and mobile - stacked on mobile, horizontal on desktop */}
            <Stack 
              width="100%" 
              direction={isMobile ? "column" : "row"} 
              spacing={fieldSpacing}
            >
              <Field label="Country Code" flex={1} width="100%">
                <CountrySelector onChange={setCurrentCountryCode} />
              </Field>
              <Field 
                label="Mobile No." 
                invalid={!!errors?.mobile} 
                flex={1}
                width="100%"
              >
                <Input 
                  px={2} 
                  name="mobile" 
                  value={formData?.mobile} 
                  onChange={handleChange} 
                  width="100%"
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                  color={theme.inputText}
                  _placeholder={{ color: theme.inputPlaceholder }}
                  _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
                />
              </Field>
            </Stack>
            {errors?.mobile && (
              <Box mt={1} width="100%">
                <Text fontSize="sm" color="red.500">
                  {errors?.mobile}
                </Text>
              </Box>
            )}
          </Box>

          {/* Promo and Referral codes - stacked on mobile, horizontal on desktop */}
          <Stack 
            width="100%" 
            direction={isMobile ? "column" : "row"} 
            spacing={fieldSpacing}
            align="flex-start"
          >
            <Field 
              label="Promo Code" 
              invalid={!!errors?.promoCode} 
              errorText={errors?.promoCode} 
              flex={1}
              width="100%"
            >
              <Input 
                px={2} 
                name="promoCode" 
                value={formData?.promoCode} 
                onChange={handleChange} 
                placeholder="Optional" 
                width="100%"
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.inputText}
                _placeholder={{ color: theme.inputPlaceholder }}
                _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
              />
            </Field>

            <Field 
              label="Referral Code" 
              invalid={!!errors?.referralCode} 
              errorText={errors?.referralCode} 
              flex={1}
              width="100%"
            >
              <Input 
                px={2} 
                name="referralBy" 
                value={formData?.referralBy} 
                onChange={handleChange} 
                placeholder="Optional" 
                width="100%"
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.inputText}
                _placeholder={{ color: theme.inputPlaceholder }}
                _focus={{ borderColor: theme.inputBorderFocus, boxShadow: `0 0 0 1px ${theme.inputBorderFocus}` }}
              />
            </Field>
          </Stack>
          
          <Box width="100%">
            <TermsAndConditions isChecked={isChecked} setIsChecked={setIsChecked} />
          </Box>

          <CommonButton
            text="Submit"
            type="submit"
            disabled={!(isChecked && !isDisabled)}
            variant={!(isChecked && !isDisabled) ? 'default' : 'gradient'}
            color={resolvedTheme === "dark" ? "var(--themeColor)" : undefined}
            width={buttonWidth}
          />
          
          <HStack 
            fontSize="md" 
            flexWrap="wrap" 
            justifyContent="center"
            width="100%"
          >
            <Text color={theme.textSecondary}>Already have an account?</Text>
            <Link 
              color={theme.textLink} 
              _hover={{ color: theme.textLinkHover }}
              onClick={() => navigate?.("/login")}
            >
              Login
            </Link>
          </HStack>
        </Stack>
      </form>

      {/* Custom Role Selection Modal */}
      {showRoleModal && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg={theme.bgOverlay}
          zIndex="999"
          justify="center"
          align="center"
          backdropFilter="blur(6px)"
          transition="all 0.3s ease-in-out"
        >
          <Box
            bg={theme.bgModal}
            color={theme.textPrimary}
            p={{ base: 6, sm: 8 }}
            borderRadius="lg"
            maxW={{ base: "90%", sm: "sm" }}
            w="100%"
            boxShadow={theme.shadowElevated}
            border="1px solid"
            borderColor={theme.borderSecondary}
            transition="all 0.3s ease"
          >
            <Text
              fontSize={{ base: "xl", sm: "2xl" }}
              fontWeight="bold"
              mb={3}
              textAlign="center"
              p={2}
              borderRadius="md"
            >
              Select Role
            </Text>
      
            <Text
              mb={6}
              textAlign="center"
              fontSize="md"
              color={theme.textTertiary}
              p={2}
              borderRadius="md"
            >
              Please choose how you want to sign up:
            </Text>
      
            <Button
              bg={theme.brandPrimary}
              color={theme.buttonPrimaryText}
              mb={3}
              w="100%"
              size="lg"
              borderRadius="full"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.3)"
              _hover={{
                opacity: 0.9,
                transform: "scale(1.02)",
                boxShadow: theme.shadowButton,
              }}
              onClick={() => handleRoleSelect("IB")}
            >
              Sign up as IB
            </Button>
      
            <Button
              bg={theme.success}
              color="white"
              mb={3}
              w="100%"
              size="lg"
              borderRadius="full"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.3)"
              _hover={{
                opacity: 0.9,
                transform: "scale(1.02)",
                boxShadow: "md",
              }}
              onClick={() => handleRoleSelect("User")}
            >
              Sign up as User
            </Button>
      
            <Button
              variant="ghost"
              w="100%"
              size="md"
              mt={1}
              borderRadius="md"
              border="1px solid"
              borderColor={theme.borderSecondary}
              color={theme.textTertiary}
              _hover={{
                bg: theme.bgTertiary,
                transform: "scale(1.01)",
              }}
              onClick={() => setShowRoleModal(false)}
            >
              Cancel
            </Button>
          </Box>
        </Flex>
      )}
    </>
  );
};